import { useTranslation } from 'react-i18next';
import {
    HStack,
    VStack,
    Text,
    Checkbox,
    Button,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberDecrementStepper,
    NumberIncrementStepper,
    SimpleGrid,
    Divider,
    Box,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';
import { useSnapStore, SnapType } from '../../tools/snapping/SnapManager';

const SNAP_TYPE_LABELS: Record<SnapType, string> = {
    endpoint: 'Endpoint',
    midpoint: 'Midpoint',
    center: 'Center',
    node: 'Node',
    quadrant: 'Quadrant',
    intersection: 'Intersection',
    extension: 'Extension',
    perpendicular: 'Perpendicular',
    tangent: 'Tangent',
    nearest: 'Nearest',
    parallel: 'Parallel',
};

const SNAP_TYPE_ORDER: SnapType[] = [
    'endpoint',
    'midpoint',
    'center',
    'node',
    'quadrant',
    'intersection',
    'extension',
    'perpendicular',
    'tangent',
    'nearest',
    'parallel',
];

export function SnapsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'snaps');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const enabled = useSnapStore((state) => state.enabled);
    const aperture = useSnapStore((state) => state.aperture);
    const gridSize = useSnapStore((state) => state.gridSize);
    const types = useSnapStore((state) => state.types);
    const setEnabled = useSnapStore((state) => state.setEnabled);
    const setAperture = useSnapStore((state) => state.setAperture);
    const setGridSize = useSnapStore((state) => state.setGridSize);
    const setSnapType = useSnapStore((state) => state.setSnapType);
    const enableAll = useSnapStore((state) => state.enableAll);
    const disableAll = useSnapStore((state) =>state.disableAll); return (<ManageDialogFrame
            title={t('manage.snaps')}
            isOpen={isOpen}
            onClose={onClose}
            footer={
                <ManageDialogCloseFooter
                    onClose={onClose}
                    extra={
                        <>
                            <Button variant="ghost" size="sm" onClick={enableAll} color="var(--color-text-secondary)">{t('manage.selectAll')}</Button>
                            <Button variant="ghost" size="sm" onClick={disableAll} color="var(--color-text-secondary)">{t('manage.clearAll')}</Button>
                        </>
                    }
                />
            }
        >
            <VStack spacing={4} align="stretch">
                <Checkbox
                    isChecked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    colorScheme="blue"
                >
                    <Text fontSize="sm">{t('manage.objectSnapsOn')}</Text>
                </Checkbox>

                <Divider borderColor="var(--color-border)" />

                <SimpleGrid columns={2} spacing={2}>
                    {SNAP_TYPE_ORDER.map((type) => (
                        <Checkbox
                            key={type}
                            isChecked={types[type]}
                            onChange={(e) => setSnapType(type, e.target.checked)}
                            colorScheme="blue"
                            isDisabled={!enabled}
                        >
                            <Text fontSize="sm">{SNAP_TYPE_LABELS[type]}</Text>
                        </Checkbox>
                    ))}
                </SimpleGrid>

                <Divider borderColor="var(--color-border)" />

                <HStack justify="space-between">
                    <Text fontSize="sm" color="var(--color-text-secondary)" whiteSpace="nowrap">{t('manage.aperturePx')}</Text>
                    <NumberInput
                        size="sm"
                        width="140px"
                        min={1}
                        max={50}
                        value={aperture}
                        onChange={(_, value) => setAperture(value || 1)}
                        isDisabled={!enabled}
                    >
                        <NumberInputField bg="var(--color-bg-tertiary)" borderColor="var(--color-border)" />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </HStack>

                <HStack justify="space-between">
                    <Text fontSize="sm" color="var(--color-text-secondary)" whiteSpace="nowrap">{t('manage.gridSpacingM')}</Text>
                    <NumberInput
                        size="sm"
                        width="140px"
                        min={0.1}
                        max={10}
                        step={0.1}
                        precision={2}
                        value={gridSize}
                        onChange={(_, value) => setGridSize(value || 0.5)}
                        isDisabled={!enabled}
                    >
                        <NumberInputField bg="var(--color-bg-tertiary)" borderColor="var(--color-border)" />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </HStack>

                <Box>
                    <Text fontSize="xs" color="var(--color-text-secondary)">{t('manage.snappingAppliesWhileDrawingAndModifyingElementsInTheViewport')}</Text>
                </Box>
            </VStack>
        </ManageDialogFrame>
    );
}
